import React, { useState, useEffect } from 'react';
import { X, Info, Shield, BarChart4, Mail } from 'lucide-react';

const CookieConsent: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [functionalEnabled, setFunctionalEnabled] = useState(true); // Always enabled, not user-controllable
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  // Check if consent has already been given
  useEffect(() => {
    const hasConsent = localStorage.getItem('cookieConsent');
    if (!hasConsent) {
      setShowBanner(true);
    } else {
      const consentSettings = JSON.parse(hasConsent);
      setAnalyticsEnabled(consentSettings.analytics);
      setFunctionalEnabled(consentSettings.functional);
      setMarketingEnabled(consentSettings.marketing);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentSettings = {
      analytics: true,
      functional: true, // Always true for essential functionality
      marketing: true
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentSettings));
    
    // Enable Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        functionality_storage: 'granted',
        personalization_storage: 'granted',
        ad_storage: 'granted'
      });
    }
    
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const consentSettings = {
      analytics: false,
      functional: true, // Always true - functional cookies are essential
      marketing: false
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentSettings));
    
    // Disable Google Analytics
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        ad_storage: 'denied'
      });
    }
    
    setShowBanner(false);
  };

  const handleSaveSettings = () => {
    const consentSettings = {
      analytics: analyticsEnabled,
      functional: true, // Always true - functional cookies are essential
      marketing: marketingEnabled
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consentSettings));
    
    // Update Google Analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: analyticsEnabled ? 'granted' : 'denied',
        functionality_storage: functionalEnabled ? 'granted' : 'denied',
        personalization_storage: marketingEnabled ? 'granted' : 'denied',
        ad_storage: marketingEnabled ? 'granted' : 'denied'
      });
    }
    
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
          {!showSettings ? (
            <div className="p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex-1">
                  <div className="flex items-center mb-3">
                    <div className="bg-purple-100 p-2 rounded-full mr-3">
                      <Info size={20} className="text-purple-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Cookies på festutrustning.se</h3>
                  </div>
                  
                  <p className="text-gray-600 mb-4">
                    Vi använder cookies för att förbättra din upplevelse, analysera trafik och personalisera innehåll. 
                    Du kan välja vilka cookies du vill tillåta.
                  </p>
                  
                  <div className="flex flex-wrap gap-2">
                    <a 
                      href="/privacy" 
                      target="_blank" 
                      className="text-sm text-purple-600 hover:text-purple-800 underline"
                    >
                      Läs vår integritetspolicy
                    </a>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => setShowSettings(true)}
                    className="px-4 py-2 border border-gray-300 rounded-none text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Anpassa
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-none transition-colors text-sm font-medium"
                  >
                    Acceptera alla
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center">
                  <div className="bg-purple-100 p-2 rounded-full mr-3">
                    <Info size={20} className="text-purple-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">Cookie-inställningar</h3>
                </div>
                <button 
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-none flex items-start">
                  <Shield className="text-green-600 mt-1 mr-3 flex-shrink-0" size={20} />
                  <div>
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-800">Nödvändiga cookies</h4>
                      <div className="relative inline-block w-10 mr-2 align-middle">
                        <input 
                          type="checkbox" 
                          checked={true} 
                          disabled
                          className="peer sr-only"
                          id="necessary-cookies"
                        />
                        <label 
                          htmlFor="necessary-cookies"
                          className="block h-6 w-11 rounded-full bg-gray-300 peer-checked:bg-green-500 cursor-not-allowed"
                        ></label>
                        <span className="absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:left-6"></span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Dessa cookies är nödvändiga för att webbplatsen ska fungera och kan inte stängas av. De lagrar inga personuppgifter.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-none flex items-start">
                  <BarChart4 className="text-blue-600 mt-1 mr-3 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-800">Analyscookies</h4>
                      <div className="relative inline-block w-10 mr-2 align-middle">
                        <input 
                          type="checkbox" 
                          checked={analyticsEnabled} 
                          onChange={() => setAnalyticsEnabled(!analyticsEnabled)}
                          id="analytics-cookies"
                          className="peer sr-only"
                        />
                        <label 
                          htmlFor="analytics-cookies"
                          className="block h-6 w-11 rounded-full bg-gray-300 peer-checked:bg-purple-600 cursor-pointer"
                        ></label>
                        <span className="absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:left-6"></span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Hjälper oss förstå hur besökare använder webbplatsen. Detta hjälper oss att förbättra användarupplevelsen.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-none flex items-start">
                  <Info className="text-purple-600 mt-1 mr-3 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-800">Funktionella cookies</h4>
                      <div className="relative inline-block w-10 mr-2 align-middle">
                        <input 
                          type="checkbox" 
                          checked={true} 
                          disabled
                          className="peer sr-only"
                          id="functional-cookies"
                        />
                        <label 
                          htmlFor="functional-cookies"
                          className="block h-6 w-11 rounded-full bg-purple-600 cursor-not-allowed"
                        ></label>
                        <span className="absolute top-1 left-6 bg-white w-4 h-4 rounded-full"></span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Nödvändiga för webbplatsens grundläggande funktioner som varukorg och bokningar. Kan inte avaktiveras.
                    </p>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-none flex items-start">
                  <Mail className="text-orange-600 mt-1 mr-3 flex-shrink-0" size={20} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-800">Marknadsföringscookies</h4>
                      <div className="relative inline-block w-10 mr-2 align-middle">
                        <input 
                          type="checkbox" 
                          checked={marketingEnabled} 
                          onChange={() => setMarketingEnabled(!marketingEnabled)}
                          id="marketing-cookies"
                          className="peer sr-only"
                        />
                        <label 
                          htmlFor="marketing-cookies"
                          className="block h-6 w-11 rounded-full bg-gray-300 peer-checked:bg-purple-600 cursor-pointer"
                        ></label>
                        <span className="absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-all peer-checked:left-6"></span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Används för att visa relevanta annonser baserat på dina intressen.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-4 py-2 border border-gray-300 rounded-none text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-none transition-colors text-sm font-medium"
                >
                  Spara inställningar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;